/**
 * Deliveries list page — FE-21.
 * Filter by source_type, user_id; paginated table with expand for detail.
 * Actions: Reindex / Unhide / Unpin via ConfirmDialog.
 */

import { useCallback, useEffect, useState } from "react";
import {
  type DeliveryDetail,
  type DeliverySummary,
  type SourceType,
  fetchDeliveries,
  fetchDeliveryDetail,
  reindexDelivery,
  unhideDelivery,
  unpinDelivery,
} from "../../api/admin-deliveries";
import { ApiError } from "../../api/client";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import {
  Pagination,
  Td,
  Th,
  fmtDate,
  shortId,
  tableStyles,
} from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { getLocale, t } from "../../i18n";
import { resolveAdminErrorMessage } from "../../lib/admin-error-messages";

const PAGE_SIZE = 20;
const SOURCE_OPTIONS = ["all", "artifact", "file", "media", "link"] as const;

type DialogAction = "reindex" | "unhide" | "unpin";

export function DeliveriesPage(): React.JSX.Element {
  const [sourceType, setSourceType] = useState<string>("all");
  const [userId, setUserId] = useState("");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<DeliverySummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DeliveryDetail | null>(null);
  const [dialog, setDialog] = useState<{
    action: DialogAction;
    id: string;
  } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchDeliveries({
        source_type:
          sourceType === "all" ? undefined : (sourceType as SourceType),
        user_id: userId || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [sourceType, userId, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSearch = useCallback((): void => {
    setOffset(0);
  }, []);

  const handleExpand = useCallback(
    async (id: string): Promise<void> => {
      if (expandedId === id) {
        setExpandedId(null);
        setDetail(null);
        return;
      }
      setExpandedId(id);
      try {
        setDetail(await fetchDeliveryDetail(id));
      } catch {
        setDetail(null);
      }
    },
    [expandedId],
  );

  const handleDialogConfirm = useCallback(
    async (reason: string): Promise<void> => {
      if (!dialog) return;
      try {
        if (dialog.action === "reindex")
          await reindexDelivery(dialog.id, reason);
        else if (dialog.action === "unhide")
          await unhideDelivery(dialog.id, reason);
        else await unpinDelivery(dialog.id, reason);
        setDialog(null);
        void load();
      } catch (e) {
        if (e instanceof ApiError) {
          setError(resolveAdminErrorMessage(e.errorCode, getLocale()));
        } else {
          setError(e instanceof Error ? e.message : t("common.action_failed"));
        }
        setDialog(null);
      }
    },
    [dialog, load],
  );

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <PageHeader
        title={t("deliveries.title")}
        description={t("deliveries.description")}
      />
      <StatsBar items={[{ label: t("stats.total"), value: total }]} />
      <FilterBar
        sourceType={sourceType}
        userId={userId}
        onSourceChange={(v) => {
          setSourceType(v);
          setOffset(0);
        }}
        onUserIdChange={setUserId}
        onSearch={handleSearch}
      />
      {error && <p style={s.error}>{error}</p>}
      {loading ? (
        <p style={s.loadingText}>{t("common.loading")}</p>
      ) : (
        <>
          <DeliveryTable
            items={items}
            expandedId={expandedId}
            detail={detail}
            onExpand={handleExpand}
            onAction={(action, id) => setDialog({ action, id })}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={(p) => setOffset((p - 1) * PAGE_SIZE)}
          />
        </>
      )}
      <ConfirmDialog
        open={dialog !== null}
        title={dialogTitle(dialog?.action ?? null)}
        description={dialogDescription(dialog?.action ?? null)}
        reasonRequired
        reasonMinLength={10}
        onConfirm={(r) => void handleDialogConfirm(r)}
        onCancel={() => setDialog(null)}
      />
    </div>
  );
}

// ─── FilterBar ───

function FilterBar(props: {
  sourceType: string;
  userId: string;
  onSourceChange: (v: string) => void;
  onUserIdChange: (v: string) => void;
  onSearch: () => void;
}): React.JSX.Element {
  return (
    <div style={s.filterRow}>
      <select
        value={props.sourceType}
        onChange={(e) => props.onSourceChange(e.target.value)}
        style={s.filterSelect}
        aria-label="Filter by source type"
      >
        {SOURCE_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {v === "all" ? t("deliveries.all_types") : capitalize(v)}
          </option>
        ))}
      </select>
      <input
        type="text"
        placeholder={t("deliveries.user_id_placeholder")}
        value={props.userId}
        onChange={(e) => props.onUserIdChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") props.onSearch();
        }}
        style={s.filterInput}
      />
      <button type="button" onClick={props.onSearch} style={s.filterBtn}>
        Search
      </button>
    </div>
  );
}

// ─── Table ───

const COLUMN_KEYS = [
  "deliveries.col_title",
  "deliveries.col_type",
  "deliveries.col_user",
  "deliveries.col_conversation",
  "deliveries.col_pinned",
  "deliveries.col_hidden",
  "deliveries.col_deleted",
  "deliveries.col_created",
] as const;

function DeliveryTable(props: {
  items: DeliverySummary[];
  expandedId: string | null;
  detail: DeliveryDetail | null;
  onExpand: (id: string) => void;
  onAction: (action: DialogAction, id: string) => void;
}): React.JSX.Element {
  if (props.items.length === 0) {
    return <p style={s.empty}>{t("deliveries.no_deliveries_found")}</p>;
  }
  return (
    <table style={s.table}>
      <thead>
        <tr>
          {COLUMN_KEYS.map((c) => (
            <Th key={c}>{t(c)}</Th>
          ))}
        </tr>
      </thead>
      <tbody>
        {props.items.map((d) => (
          <DeliveryRows
            key={d.id}
            item={d}
            isExpanded={props.expandedId === d.id}
            detail={props.expandedId === d.id ? props.detail : null}
            onExpand={props.onExpand}
            onAction={props.onAction}
          />
        ))}
      </tbody>
    </table>
  );
}

function DeliveryRows(props: {
  item: DeliverySummary;
  isExpanded: boolean;
  detail: DeliveryDetail | null;
  onExpand: (id: string) => void;
  onAction: (action: DialogAction, id: string) => void;
}): React.JSX.Element {
  const { item: d } = props;
  return (
    <>
      <tr
        style={s.clickableRow}
        onClick={() => props.onExpand(d.id)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") props.onExpand(d.id);
        }}
      >
        <Td>{truncate(d.title, 40)}</Td>
        <Td>
          <SourceBadge type={d.source_type} />
        </Td>
        <Td mono>{shortId(d.user_id)}</Td>
        <Td mono>{shortId(d.conversation_id)}</Td>
        <Td>{d.pinned ? t("common.yes") : t("common.no")}</Td>
        <Td>{d.hidden ? t("common.yes") : t("common.no")}</Td>
        <Td>{d.deleted_at ? fmtDate(d.deleted_at) : "-"}</Td>
        <Td>{fmtDate(d.created_at)}</Td>
      </tr>
      {props.isExpanded && (
        <tr>
          <td colSpan={COLUMN_KEYS.length} style={s.expandedCell}>
            <ExpandedDetail
              detail={props.detail}
              item={d}
              onAction={props.onAction}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Expanded detail ───

function ExpandedDetail(props: {
  detail: DeliveryDetail | null;
  item: DeliverySummary;
  onAction: (action: DialogAction, id: string) => void;
}): React.JSX.Element {
  const { detail, item } = props;
  if (!detail)
    return <p style={s.loadingText}>{t("deliveries.loading_detail")}</p>;

  return (
    <div style={s.detailPanel}>
      <div style={s.detailGrid}>
        <DetailField
          label={t("deliveries.detail_user")}
          value={detail.user_name}
        />
        <DetailField
          label={t("deliveries.detail_conversation")}
          value={detail.conversation_title ?? "-"}
        />
        <DetailField
          label={t("deliveries.detail_mime")}
          value={detail.mime_type ?? "-"}
        />
        <DetailField
          label={t("deliveries.detail_size")}
          value={formatBytes(detail.size_bytes)}
        />
        <DetailField
          label={t("deliveries.detail_updated")}
          value={fmtDate(detail.updated_at)}
        />
      </div>
      <div style={s.detailActions}>
        <button
          type="button"
          style={s.actionBtn}
          onClick={() => props.onAction("reindex", item.id)}
        >
          {t("deliveries.reindex")}
        </button>
        {item.hidden && (
          <button
            type="button"
            style={s.actionBtn}
            onClick={() => props.onAction("unhide", item.id)}
          >
            {t("deliveries.unhide")}
          </button>
        )}
        {item.pinned && (
          <button
            type="button"
            style={s.actionBtn}
            onClick={() => props.onAction("unpin", item.id)}
          >
            {t("deliveries.unpin")}
          </button>
        )}
      </div>
    </div>
  );
}

function DetailField(props: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div>
      <span style={s.detailLabel}>{props.label}</span>
      <span style={s.detailValue}>{props.value}</span>
    </div>
  );
}

// ─── SourceBadge ───

function SourceBadge({ type }: { type: SourceType }): React.JSX.Element {
  const c = SOURCE_COLOR[type];
  return (
    <span style={{ ...s.badge, background: c.bg, color: c.fg }}>
      {capitalize(type)}
    </span>
  );
}

const SOURCE_COLOR: Record<SourceType, { bg: string; fg: string }> = {
  artifact: { bg: "#dbeafe", fg: "#1e40af" },
  file: { bg: "#dcfce7", fg: "#166534" },
  media: { bg: "#fef9c3", fg: "#854d0e" },
  link: { bg: "#f3e8ff", fg: "#6b21a8" },
};

// ─── Dialog helpers ───

function dialogTitle(action: DialogAction | null): string {
  if (action === "reindex") return t("deliveries.reindex_title");
  if (action === "unhide") return t("deliveries.unhide_title");
  if (action === "unpin") return t("deliveries.unpin_title");
  return "";
}

function dialogDescription(action: DialogAction | null): string {
  if (action === "reindex") return t("deliveries.reindex_desc");
  if (action === "unhide") return t("deliveries.unhide_desc");
  if (action === "unpin") return t("deliveries.unpin_desc");
  return "";
}

// ─── Helpers ───

function capitalize(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function truncate(str: string, max: number): string {
  return str.length > max ? `${str.slice(0, max)}...` : str;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1_024).toFixed(0)} KB`;
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  ...tableStyles,
  filterSelect: {
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
    background: "var(--color-bg)",
  },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
  },
  clickableRow: { cursor: "pointer" },
  empty: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
    padding: 24,
    textAlign: "center",
  },
  expandedCell: {
    padding: 0,
    background: "var(--color-bg-secondary)",
    borderBottom: "1px solid var(--color-border)",
  },
  detailPanel: { padding: "16px 24px" },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 12,
  },
  detailLabel: {
    display: "block",
    fontSize: 11,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    marginBottom: 2,
  },
  detailValue: { display: "block", fontSize: 13 },
  detailActions: { display: "flex", gap: 8, marginTop: 8 },
  actionBtn: {
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    cursor: "pointer",
  },
};
